import * as Sentry from "@sentry/react";
import { z } from "zod";

const MAX_UNION_DEPTH = 2;
const MAX_SAFE_ISSUES = 20;

/**
 * A Sentry-safe projection of a Zod issue: field path, issue code, message, and
 * schema-side metadata (`expected`, `values`). We deliberately drop offending
 * `input` values so account numbers, balances, and other PII never reach Sentry
 * — this mirrors the redaction contract the old `ensureArray` helper upheld.
 */
interface LooseParseIssue {
  path: string;
  code: string;
  message: string;
  expected?: string;
  values?: readonly string[];
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const issuePath = (issue: z.core.$ZodIssue): string =>
  issue.path.map((segment) => String(segment)).join(".");

const flattenIssue = (
  issue: z.core.$ZodIssue,
  depth: number,
  pathPrefix = "",
): LooseParseIssue[] => {
  const path = pathPrefix
    ? pathPrefix + (issuePath(issue) ? `.${issuePath(issue)}` : "")
    : issuePath(issue);

  const base: LooseParseIssue = {
    path,
    code: issue.code,
    message: issue.message,
  };

  if (issue.code === "invalid_type") {
    base.expected = String(issue.expected);
  }

  if (issue.code === "invalid_value") {
    base.values = issue.values.map(String);
  }

  if (issue.code === "invalid_union" && depth < MAX_UNION_DEPTH) {
    // Zod arm issues use paths *relative to the union*, so prefix with the
    // union's own path then the arm index — never re-append that path from
    // the child (which would produce `union[0].history.history.timestamp`).
    const nested = issue.errors.flatMap((arm, index) => {
      const armPrefix = path ? `${path}.union[${index}]` : `union[${index}]`;
      return arm.flatMap((sub) => flattenIssue(sub, depth + 1, armPrefix));
    });
    return [base, ...nested];
  }

  return [base];
};

const dedupeKey = (issue: LooseParseIssue): string =>
  `${issue.path}\0${issue.code}`;

const capSafeIssues = (
  issues: LooseParseIssue[],
  dedupe: boolean,
): { issues: LooseParseIssue[]; truncated?: number } => {
  const unique: LooseParseIssue[] = [];
  const seen = new Set<string>();

  for (const issue of issues) {
    if (dedupe) {
      const key = dedupeKey(issue);
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
    }
    unique.push(issue);
  }

  if (unique.length <= MAX_SAFE_ISSUES) {
    return { issues: unique };
  }

  return {
    issues: unique.slice(0, MAX_SAFE_ISSUES),
    truncated: unique.length - MAX_SAFE_ISSUES,
  };
};

const flattenIssues = (
  issues: readonly z.core.$ZodIssue[],
): LooseParseIssue[] => issues.flatMap((issue) => flattenIssue(issue, 0));

const toSafeIssuesWithMeta = (
  issues: readonly z.core.$ZodIssue[],
): { issues: LooseParseIssue[]; truncated?: number } =>
  capSafeIssues(flattenIssues(issues), true);

/**
 * Non-fatal runtime validation of an API response against a Zod schema.
 *
 * On success the parsed (schema-shaped) value is returned. On failure the raw
 * payload is returned untouched — validation never blocks a query or mutation —
 * and a single grouped Sentry warning is emitted so we learn about broken API
 * contracts without breaking the app. Warnings are grouped under one category
 * (`loose-parse`) and sub-grouped per endpoint via the `context` fingerprint,
 * mirroring the `ensureArray` / suppressed-401 precedent.
 *
 * Keep strict `.parse` wherever a hard failure is genuinely desired (e.g.
 * `authxToken`); this helper is for the "log, don't block" case.
 *
 * CONTRACT CAVEAT: on failure the return value is the *raw* payload cast to
 * `z.output<Schema>`, so it is NOT guaranteed to match the declared type —
 * transforms/coercions (e.g. `z.coerce.date()`) did not run. Callers that
 * immediately dereference coerced or structurally-required fields on the
 * result (`.getTime()`, `.map(...)`, `"x" in result`) must guard against a
 * non-conforming shape; otherwise the "never blocks" promise is broken on the
 * failure path. See `getPortfolioHistory` for the defensive pattern.
 */
export const looseParse = <Schema extends z.ZodType>(
  schema: Schema,
  data: unknown,
  context: string,
): z.output<Schema> => {
  const result = schema.safeParse(data);

  if (result.success) {
    return result.data;
  }

  const { issues, truncated } = toSafeIssuesWithMeta(result.error.issues);

  Sentry.captureMessage("API response failed schema validation", {
    level: "warning",
    tags: {
      component: "loose-parse",
      context,
    },
    // Collapse every failure for a given endpoint into one Sentry issue rather
    // than one issue per differing payload shape.
    fingerprint: ["loose-parse-failure", context],
    extra: {
      context,
      issues,
      ...(truncated !== undefined ? { truncated } : {}),
    },
  });

  return data as z.output<Schema>;
};

/**
 * Schema combinator for response arrays where one malformed item shouldn't
 * take down its valid siblings. `z.array(itemSchema).catch([])` degrades the
 * *entire* array to `[]` the moment a single item fails — one new enum value
 * or unexpected null can silently erase every valid record (accounts, roles,
 * transfers, ...) with no warning. `looseArray` instead parses each item
 * independently: valid items come back schema-shaped, invalid items are kept
 * as their raw payload, and a single grouped Sentry warning fires (only) when
 * at least one item failed. Array length therefore always matches the
 * response. A non-array/missing top-level value still degrades to `[]`,
 * matching the old `ensureArray` contract.
 *
 * The one exception is a failed item that isn't a plain record — `null`,
 * `undefined`, a primitive, or an array: there is no field data in it to
 * salvage, and it's the shape that reliably breaks consumers, since both
 * property access and the in-place normalization some REST fns do
 * (`getRapydTransfers`) throw or silently read `undefined` off it. Those are
 * dropped and counted separately as `droppedCount` in the Sentry warning.
 *
 * CONTRACT CAVEAT: exactly as in `looseParse`, a failed item is the *raw*
 * value cast to `z.output<Item>`, so it is NOT guaranteed to match the
 * declared type — transforms/coercions (e.g. `z.coerce.date()`) did not run
 * and required fields may be missing entirely. A caller reading a field with
 * a string/number/Date method (`.toUpperCase()`, `.map()`, `.toFixed()`) can
 * therefore hit `undefined` at runtime despite a non-optional type, and one
 * bad row is enough to take down a whole table. Guard those reads.
 *
 * Use this wherever `z.array(recordSchema).catch([])` would otherwise be
 * used; keep plain `z.array(x).catch([])` only for arrays of bare
 * primitives, where there's no meaningful per-item reporting to do.
 */
export const looseArray = <Item extends z.ZodType>(
  itemSchema: Item,
  context: string,
) =>
  z
    .preprocess((data) => {
      if (Array.isArray(data)) {
        return data;
      }
      // `null`/`undefined` is a normal "no data" response and degrades to `[]`
      // silently. Any other non-array is a contract break worth surfacing —
      // this restores the warning the old `ensureArray` emitted on non-null
      // non-arrays (the previous `.catch([])` swallowed it silently).
      if (data != null) {
        Sentry.captureMessage("API response was not an array", {
          level: "warning",
          tags: {
            component: "loose-parse",
            context,
          },
          fingerprint: ["loose-parse-non-array", context],
          extra: {
            context,
            receivedType: typeof data,
          },
        });
      }
      return [];
    }, z.array(z.unknown()))
    .transform((items) => {
      const parsed: z.output<Item>[] = [];
      const rawIssues: LooseParseIssue[] = [];
      let invalidCount = 0;
      let droppedCount = 0;

      for (const item of items) {
        const result = itemSchema.safeParse(item);
        if (result.success) {
          parsed.push(result.data);
          continue;
        }

        invalidCount += 1;
        // Flatten only — cap once over the aggregate so `truncated` reflects
        // every omitted issue across all items, not a silent per-item drop.
        rawIssues.push(...flattenIssues(result.error.issues));

        if (isRecord(item)) {
          // Keep the record: a stale schema must never hide data the UI needs.
          // The cast is unsound by design — see the CONTRACT CAVEAT above.
          parsed.push(item as z.output<Item>);
        } else {
          droppedCount += 1;
        }
      }

      if (invalidCount > 0) {
        const { issues, truncated } = capSafeIssues(rawIssues, false);

        Sentry.captureMessage(
          "API response array item failed schema validation",
          {
            level: "warning",
            tags: {
              component: "loose-parse",
              context,
            },
            // Group all bad-item warnings for a given endpoint into one Sentry
            // issue, distinct from whole-payload `looseParse` failures.
            fingerprint: ["loose-parse-array-item-failure", context],
            extra: {
              context,
              // Items that failed validation, and of those the ones removed for
              // not being records. Neither is the number of Zod issues, which
              // can be many per item.
              invalidCount,
              droppedCount,
              issues,
              ...(truncated !== undefined ? { truncated } : {}),
            },
          },
        );
      }

      return parsed;
    });
